/**
 * RecordSelect — searchable multiselect dropdown zaprojektowany do reużycia dla
 * pól referencyjnych (na razie: filtr kolumny „Firma"; docelowo inne pola typu
 * `reference` — poza zakresem tej zmiany).
 *
 * RÓŻNICA od `MultiSelect` (NIE modyfikujemy tamtego pliku — jest używany przez
 * pola formularza akceptantów procesu, gdzie auto-select-przy-pisaniu byłby
 * katastrofalny): tu wpisanie tekstu w search ma DWA efekty na dwóch prędkościach:
 *
 *  1. Natychmiastowe zawężenie widocznej listy opcji (diakrytyka-insensitive,
 *     `normalizeSearch`) — bez debounce, czysto wizualne.
 *  2. Debounced (domyślnie 300ms) auto-zaznaczenie: gdy search jest NIEPUSTY i
 *     „osiadł" (nie zmienił się przez `debounceMs`), `onChange` dostaje DOKŁADNIE
 *     zbiór aktualnie widocznych (przefiltrowanych) opcji — semantyka REPLACE, nie
 *     dołączenie. To ten callback aplikuje filtr (→ refetch listy), stąd debounce:
 *     bez niego każdy klawisz odpalałby refetch. Search pusty → BRAK auto-selecta
 *     (zostawiamy dotychczasową selekcję bez zmian).
 *
 * Każda RĘCZNA akcja (toggle checkboxa opcji, X na chipie, przycisk „zaznacz/
 * odznacz widoczne") jest NATYCHMIASTOWA i KASUJE oczekujący debounce — użytkownik
 * przejął stery, więc spóźniony auto-select nie może nadpisać jego decyzji.
 * Zaimplementowane przez ref na timer (`debounceTimerRef`), czyszczony jawnie w
 * KAŻDYM ręcznym handlerze, niezależnie od efektu planującego debounce (ten
 * odpala się WYŁĄCZNIE przy zmianie `search`, nie przy zmianie identity
 * `onChange`/`options` z rodzica — stąd `onChangeRef`/`filteredOptionsRef`).
 *
 * Wzorce portalowania/pozycjonowania/click-outside/Escape/chipów skopiowane z
 * `MultiSelect.tsx` (ten sam problem: panel `fixed`+portal, bo `absolute` w
 * kontenerze ze scrollem byłby przycinany — patrz JSDoc tamtego pliku). Logika
 * selekcji jest jednak WŁASNA — MultiSelect nie ma auto-selecta ani przycisku
 * „zaznacz/odznacz widoczne".
 *
 * a11y: jak MultiSelect — outer `role="combobox"`, panel `role="listbox"` +
 * `role="option"`, chip-remove i toggle-all to osobne `<button>` (bez
 * button-w-button).
 *
 * i18n: 4 stringi reużyte z `common.multiSelect.*` (placeholder/searchPlaceholder/
 * noResults/removeAriaLabel) + 1 NOWY klucz `common.recordSelect.toggleAllVisible`.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';

import { type AnchoredPanelPosition, computeAnchoredPanelPosition } from '@/lib/anchored-panel';
import { normalizeSearch } from '@/lib/search-normalize';

export interface RecordSelectOption {
  /** Unikalna wartość (UUID typowo). */
  value: string;
  /** Wyświetlana etykieta. */
  label: string;
  /** Opcjonalny pomocniczy tekst pod labelem. */
  description?: string;
}

export interface RecordSelectProps {
  /** Aktualnie wybrane values (controlled). */
  value: ReadonlyArray<string>;
  /** Callback przy zmianie selekcji (nowa lista values) — wołany zarówno przez
   *  ręczne akcje (natychmiast) jak i przez debounced auto-select (REPLACE). */
  onChange: (value: string[]) => void;
  /** Lista wszystkich dostępnych opcji. */
  options: ReadonlyArray<RecordSelectOption>;
  /** Placeholder gdy nic nie wybrane. Default: `common.multiSelect.placeholder`. */
  placeholder?: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Klasy dla wrapper'a. */
  className?: string;
  /** Test-id na wrapperze + bazowy prefix dla chipów/opcji/przycisków wewnątrz. */
  'data-testid'?: string;
  /** Opóźnienie (ms) auto-zaznaczenia po ustaniu pisania w search. Default: 300. */
  debounceMs?: number;
  /**
   * `true` — wysokość triggera `h-9` (36px) zamiast domyślnych `min-h-[42px]`, jak
   * inne kontrolki wiersza filtrów `ConfigurableTable` (`FILTER_CONTROL_SIZE_CLASS`).
   * Domyślnie `false` — niezmieniony wygląd formularzowy (wszystkie inne usage).
   */
  compact?: boolean;
}

export function RecordSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  className,
  'data-testid': testIdProp,
  debounceMs = 300,
  compact = false,
}: RecordSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Trigger (`role="combobox"`) — cel zwrotu fokusu po zamknięciu listy klawiszem Esc.
  const comboboxRef = useRef<HTMLDivElement>(null);
  // Panel dropdownu portalem do body (position: fixed) — patrz JSDoc `MultiSelect.tsx`
  // dla uzasadnienia (obchodzi `overflow` kontenera-rodzica, np. modal ze scrollem).
  const panelRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<AnchoredPanelPosition | null>(null);
  const listboxId = useId();
  const testId = testIdProp ?? 'record-select';

  // Timer dla debounced auto-select. Jawnie czyszczony przez KAŻDĄ ręczną akcję
  // (handleToggle/handleRemove/handleToggleAllVisible) — manual action = user
  // przejął stery, spóźniony auto-select nie może go nadpisać.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPendingAutoSelect = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const valueSet = useMemo(() => new Set(value), [value]);
  const normalizedSearch = search ? normalizeSearch(search) : '';
  const filteredOptions = useMemo(
    () =>
      normalizedSearch
        ? options.filter(
            (opt) =>
              normalizeSearch(opt.label).includes(normalizedSearch) ||
              (opt.description && normalizeSearch(opt.description).includes(normalizedSearch)),
          )
        : options,
    [options, normalizedSearch],
  );

  const selectedOptions = options.filter((opt) => valueSet.has(opt.value));

  // `onChange`/`filteredOptions` przez ref — efekt niżej ma zależeć WYŁĄCZNIE od
  // `search` (planujemy/odświeżamy debounce tylko gdy user PISZE, nie przy każdej
  // zmianie identity propsów z rodzica, np. `options` z nowym query result).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const filteredOptionsRef = useRef(filteredOptions);
  filteredOptionsRef.current = filteredOptions;

  // Debounced auto-select: search niepusty + „osiadł" `debounceMs` → onChange
  // z DOKŁADNIE aktualnym zbiorem przefiltrowanych opcji (REPLACE semantyka).
  // Search pusty → brak auto-selecta (return wcześniej, bez planowania timera).
  useEffect(() => {
    clearPendingAutoSelect();
    if (!search) return;
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onChangeRef.current(filteredOptionsRef.current.map((opt) => opt.value));
    }, debounceMs);
    return clearPendingAutoSelect;
  }, [search, debounceMs, clearPendingAutoSelect]);

  const handleToggle = useCallback(
    (optValue: string) => {
      clearPendingAutoSelect();
      if (valueSet.has(optValue)) {
        onChange(value.filter((v) => v !== optValue));
      } else {
        onChange([...value, optValue]);
      }
    },
    [clearPendingAutoSelect, onChange, value, valueSet],
  );

  const handleRemove = useCallback(
    (optValue: string) => {
      clearPendingAutoSelect();
      onChange(value.filter((v) => v !== optValue));
    },
    [clearPendingAutoSelect, onChange, value],
  );

  const allVisibleSelected =
    filteredOptions.length > 0 && filteredOptions.every((opt) => valueSet.has(opt.value));

  const handleToggleAllVisible = useCallback(() => {
    if (filteredOptions.length === 0) return;
    clearPendingAutoSelect();
    if (allVisibleSelected) {
      const visibleSet = new Set(filteredOptions.map((opt) => opt.value));
      onChange(value.filter((v) => !visibleSet.has(v)));
    } else {
      const merged = new Set(value);
      filteredOptions.forEach((opt) => merged.add(opt.value));
      onChange([...merged]);
    }
  }, [allVisibleSelected, clearPendingAutoSelect, filteredOptions, onChange, value]);

  // Pozycja portalowanego panelu — liczona z prostokąta triggera; odświeżana przy
  // scrollu/resize (panel `fixed` nie jedzie z treścią, więc trzeba go przesuwać).
  useEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    const update = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuPos(computeAnchoredPanelPosition(r));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  // Close on click outside — panel jest w PORTALU (poza wrapperRef), więc klik w
  // opcję liczyłby się jako „na zewnątrz" bez wykluczenia panelRef.
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as globalThis.Node;
      const inWrapper = wrapperRef.current?.contains(target) ?? false;
      const inPanel = panelRef.current?.contains(target) ?? false;
      if (!inWrapper && !inPanel) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape. Faza przechwytywania + `preventDefault`: okno nakładkowe (`Overlay`) widzi
  // wtedy Esc jako już obsłużony i nie zamyka się razem z listą. Fokus wraca na trigger (lista jest
  // w portalu, fokus był w jej polu wyszukiwania — bez tego spadłby na `<body>`); zamknięcie
  // kliknięciem poza listą fokusu nie rusza.
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        setSearch('');
        comboboxRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  const handleTriggerKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen((v) => !v);
      }
    },
    [disabled],
  );

  const resolvedPlaceholder = placeholder ?? t('common.multiSelect.placeholder');

  return (
    <div ref={wrapperRef} className={`relative ${className ?? ''}`} data-testid={testId}>
      <div
        ref={comboboxRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen((v) => !v)}
        onKeyDown={handleTriggerKey}
        className={`flex ${compact ? 'h-9' : 'min-h-[42px]'} w-full cursor-pointer flex-wrap items-center gap-1 rounded-lg border border-border-primary bg-bg-primary px-2 ${compact ? 'py-1' : 'py-1.5'} text-left transition-colors hover:border-accent-300 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-200 dark:focus:ring-accent-800 ${
          disabled ? 'cursor-not-allowed opacity-50' : ''
        } ${isOpen ? 'border-accent-500' : ''}`}
        data-testid={`${testId}-button`}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-sm text-text-secondary">{resolvedPlaceholder}</span>
        ) : (
          selectedOptions.map((opt) => (
            <span
              key={opt.value}
              className="flex items-center gap-1 rounded bg-accent-100 px-2 py-0.5 text-xs text-accent-700 dark:bg-accent-900/40 dark:text-accent-300"
              data-testid={`${testId}-chip-${opt.value}`}
            >
              {opt.label}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(opt.value);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                disabled={disabled}
                aria-label={t('common.multiSelect.removeAriaLabel', { label: opt.label })}
                className="inline-flex h-3 w-3 items-center justify-center hover:text-red-500"
                data-testid={`${testId}-chip-remove-${opt.value}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))
        )}
        <ChevronDown
          className={`ml-auto h-4 w-4 text-text-secondary transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </div>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              // Wysokość docięta do miejsca w oknie (panel scrolluje w środku) — zamiast
              // twardego `max-h-64`, który przy polu na dole formularza wychodził za ekran.
              maxHeight: menuPos.maxHeight,
              zIndex: 60,
            }}
            className="overflow-y-auto rounded-lg border border-border-primary bg-bg-primary shadow-lg"
            data-testid={`${testId}-dropdown`}
          >
            {/*
             * Search input + toggle-all poza role="listbox" (WAI-ARIA APG: listbox
             * akceptuje tylko option/group children).
             */}
            <div className="sticky top-0 z-10 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border-primary bg-bg-primary px-2 py-1.5">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('common.multiSelect.searchPlaceholder')}
                aria-label={t('common.multiSelect.searchPlaceholder')}
                aria-controls={listboxId}
                // min-w + flex-wrap: w wąskim panelu przycisk „Zaznacz/odznacz widoczne" zawija
                // się pod pole, zamiast ściskać wyszukiwarkę do dwóch liter.
                className="min-w-[8rem] flex-1 bg-bg-primary px-1 py-1 text-sm focus:outline-none"
                autoFocus
                data-testid={`${testId}-search`}
              />
              <button
                type="button"
                onClick={handleToggleAllVisible}
                className="ml-auto shrink-0 whitespace-nowrap rounded px-2 py-1 text-xs text-accent-600 hover:bg-bg-secondary dark:text-accent-400"
                data-testid={`${testId}-toggle-all`}
              >
                {t('common.recordSelect.toggleAllVisible')}
              </button>
            </div>
            <div
              id={listboxId}
              role="listbox"
              aria-multiselectable="true"
              aria-label={resolvedPlaceholder}
            >
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-secondary">
                  {t('common.multiSelect.noResults')}
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = valueSet.has(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleToggle(opt.value)}
                      className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-bg-secondary ${
                        isSelected ? 'bg-accent-50 dark:bg-accent-900/20' : ''
                      }`}
                      data-testid={`${testId}-option-${opt.value}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        aria-hidden="true"
                        tabIndex={-1}
                        className="mt-0.5 h-4 w-4 text-accent-600"
                      />
                      <div className="flex-1">
                        <div className="text-text-primary">{opt.label}</div>
                        {opt.description && (
                          <div className="text-xs text-text-secondary">{opt.description}</div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
